<template>
  <div>
    <a-modal
      v-model="localChequeVisible"
      :title="$t('recette.detailsPaymentDist')"
      width="70%"
      @ok="handleOk"
      @cancel="handleOk"
    >
      <div class="cheque-modal-content minimal">
        <!-- Totals Section -->
        <div class="cheque-totals minimal-section">
          <div v-if="loadedInvoice.montantCheque">
            <span class="cheque-label minimal-label">
              <a-icon type="credit-card" class="mr-2" />
              {{ $t("recette.chequeMontantTotal") }}:
            </span>
            <span class="cheque-value minimal-value">{{
              loadedInvoice.montantCheque
            }}</span>
          </div>
          <div v-if="loadedInvoice.montantEspece">
            <span class="cheque-label minimal-label">
              <a-icon type="wallet" class="mr-2" />
              {{ $t("recette.montantEspece") }}:
            </span>
            <span class="cheque-value minimal-value">{{
              loadedInvoice.montantEspece
            }}</span>
          </div>
          <div v-if="loadedInvoice.montantTraite">
            <span class="cheque-label minimal-label">
              <a-icon type="bank" class="mr-2" />
              {{ $t("recette.montantTraite") }}:
            </span>
            <span class="cheque-value minimal-value">{{
              loadedInvoice.montantTraite
            }}</span>
          </div>
          <div v-if="loadedInvoice.montantTPE">
            <span class="cheque-label minimal-label">
              <a-icon type="swap" class="mr-2" />
              {{ $t("recette.montantTPE") }}:
            </span>
            <span class="cheque-value minimal-value">{{
              loadedInvoice.montantTPE
            }}</span>
          </div>
          <div v-if="loadedInvoice.montantVirement">
            <span class="cheque-label minimal-label">
              <a-icon type="upload" class="mr-2" />
              {{ $t("recette.montantVirement") }}:
            </span>
            <span class="cheque-value minimal-value">{{
              loadedInvoice.montantVirement
            }}</span>
          </div>
        </div>

        <a-divider />

        <!-- Related Payments -->
        <div v-if="loadedInvoice.details" class="minimal-section">
          <div class="minimal-title">
            <a-icon type="link" /> {{ $t("recette.relatedPayments") }}
          </div>
          <a-table
            :dataSource="loadedInvoice.details"
            :columns="relatedPaymentsColumns"
            rowKey="_id"
            size="small"
            bordered
            :pagination="false"
            :rowClassName="() => 'zebra-row'"
          >
            <template slot="paymentOnWhat" slot-scope="text, record">
              <div v-if="record.fees">
                <a-tag
                  color="blue"
                  v-for="(value, key) in record.fees"
                  :key="key"
                  >{{ formatFeeName(key, record.daily) }}</a-tag
                >
              </div>
            </template>
          </a-table>
        </div>
        <a-skeleton v-if="loading" active :paragraph="{ rows: 2 }" />
        <div v-else>
          <!-- Associated Cheques -->
          <div
            v-if="loadedChequesData[0] && loadedChequesData[0].relatedInvoice"
            class="minimal-section"
          >
            <div class="minimal-title">
              <a-icon type="credit-card" />
              {{ $t("recette.associatedCheques") }}
            </div>
            <a-table
              :dataSource="loadedChequesData"
              :columns="associatedChequesColumns"
              rowKey="_id"
              size="small"
              bordered
              :pagination="false"
              :rowClassName="() => 'zebra-row'"
            >
              <template slot="bankStatus" slot-scope="text">
                <a-tag :color="getChequeStatutColor(text)" class="status-tag">
                  {{ $t("status." + text) }}
                </a-tag>
              </template>
            </a-table>
          </div>

          <!-- Associated Traites -->
          <div
            v-if="loadedTraitesData[0] && loadedTraitesData[0].relatedInvoice"
            class="minimal-section"
          >
            <div class="minimal-title">
              <a-icon type="bank" /> {{ $t("recette.associatedTraites") }}
            </div>
            <a-table
              :dataSource="loadedTraitesData"
              :columns="associatedTraitesColumns"
              rowKey="_id"
              size="small"
              bordered
              :pagination="false"
              :rowClassName="() => 'zebra-row'"
            >
              <template slot="bankStatus" slot-scope="text">
                <a-tag :color="getChequeStatutColor(text)" class="status-tag">
                  {{ $t("status." + text) }}
                </a-tag>
              </template>
            </a-table>
          </div>
        </div>
        <!-- Associated TPE -->
        <div v-if="loadedInvoice.tpe" class="minimal-section">
          <div class="minimal-title">
            <a-icon type="swap" /> {{ $t("recette.associatedTPE") }}
          </div>
          <a-table
            :dataSource="[loadedInvoice.tpe]"
            :columns="associatedTPEColumns"
            rowKey="transactionId"
            size="small"
            bordered
            :pagination="false"
            :rowClassName="() => 'zebra-row'"
          />
        </div>

        <!-- Associated Virements -->
        <div
          v-if="loadedVirementsData[0] && loadedVirementsData[0].relatedInvoice"
          class="minimal-section"
        >
          <div class="minimal-title">
            <a-icon type="upload" />
            {{ $t("recette.associatedVirements") }}
          </div>
          <a-table
            :dataSource="loadedVirementsData"
            :columns="associatedVirementsColumns"
            rowKey="_id"
            size="small"
            bordered
            :pagination="false"
            :rowClassName="() => 'zebra-row'"
          >
            <template slot="file" slot-scope="text, record">
              <a-button
                v-if="record.file"
                type="link"
                size="small"
                @click="
                  previewFile(
                    settings.resourcesUrl.replace('FILE_PATH', record.file)
                  )
                "
                icon="eye"
              >
                {{ $t("recette.viewFile") }}
              </a-button>
              <span v-else>--</span>
            </template>
            <template slot="bankStatus" slot-scope="text">
              <a-tag :color="getChequeStatutColor(text)" class="status-tag">
                {{ $t("status." + text) }}
              </a-tag>
            </template>
          </a-table>
        </div>

        <!-- Replaced Cheques -->
        <div
          v-if="loadedReplacedChequesData.length > 0"
          class="minimal-section"
        >
          <div class="minimal-title">
            <a-icon type="swap" style="color: #52c41a" />
            {{ $t("recette.replacedCheques") }}
          </div>
          <a-table
            :dataSource="loadedReplacedChequesData"
            :columns="replacedChequesColumns"
            rowKey="_id"
            size="small"
            bordered
            :pagination="false"
            :rowClassName="() => 'zebra-row'"
          >
            <template slot="status" slot-scope="text">
              <a-tag :color="getChequeStatutColor(text)" class="status-tag">
                {{ $t("status." + text) || text }}
              </a-tag>
            </template>
            <template slot="bankStatus" slot-scope="text">
              <a-tag :color="getChequeStatutColor(text)" class="status-tag">
                {{ $t("status." + text) }}
              </a-tag>
            </template>
            <template slot="replacedBy" slot-scope="text, record">
              <div v-if="record.replacedBy">
                {{ record.replacedBy.userName }} -
                {{ moment(record.updatedAt).format("DD/MM/YYYY HH:mm") }}
              </div>
              <span v-else>--</span>
            </template>
          </a-table>
        </div>

        <!-- Loading State -->
        <div
          v-if="
            !loadedInvoice.details &&
            !loadedChequesData.length &&
            !loadedTraitesData.length &&
            !loadedVirementsData.length &&
            !loadedInvoice.tpe
          "
          class="cheque-loading"
        >
          <a-skeleton active :paragraph="{ rows: 2 }" />
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import apiClient from "@/services/axios";
import moment from "moment";
import { formatFeeName as formatFeeNameUtil } from "@/utilities/settings";
import { mapState } from "vuex";
import { getChequeStatutColor } from "@/utilities/settings";
export default {
  props: ["chequeVisible", "invoiceId"],
  async mounted() {
    this.getChequeDetails(this.invoiceId);
  },
  data() {
    return {
      loadedChequesData: [],
      loadedTraitesData: [],
      loadedVirementsData: [],
      loadedReplacedChequesData: [],
      loadedInvoice: {},
      localChequeVisible: this.chequeVisible, // Create a local copy
      loading: false,
    };
  },
  computed: {
    ...mapState(["settings"]),
    relatedPaymentsColumns() {
      return [
        {
          title: this.$t("recette.paiementQuoi"),
          dataIndex: "paymentOnWhat",
          key: "paymentOnWhat",
          scopedSlots: { customRender: "paymentOnWhat" },
        },
        {
          title: this.$t("comptabilite.nom"),
          dataIndex: "typeReglement",
          key: "typeReglement",
        },
        {
          title: this.$t("recette.montantTotal"),
          dataIndex: "montantTotal",
          key: "montantTotal",
        },
      ];
    },
    associatedChequesColumns() {
      return [
        {
          title: this.$t("recette.numero"),
          dataIndex: "num",
          key: "num",
          customRender: (text) => text || "--",
        },
        {
          title: this.$t("recette.titulaire"),
          dataIndex: "titulaire",
          key: "titulaire",
          customRender: (text) => text || "--",
        },
        {
          title: this.$t("recette.banque"),
          dataIndex: "bank",
          key: "bank",
          customRender: (text) => text || "--",
        },
        {
          title: this.$t("recette.montant"),
          dataIndex: "montant",
          key: "montant",
          customRender: (text) => Number(text).toFixed(2),
        },
        {
          title: this.$t("tresorerie.statutBanque"),
          dataIndex: "bankStatus",
          key: "bankStatus",
          scopedSlots: { customRender: "bankStatus" },
        },
        {
          title: this.$t("recette.dateEcheance"),
          dataIndex: "dateEchance",
          key: "dateEchance",
          customRender: (text) =>
            text ? moment(text).format("DD/MM/YYYY") : "--",
        },
      ];
    },
    associatedTraitesColumns() {
      return [
        {
          title: this.$t("recette.numero"),
          dataIndex: "num",
          key: "num",
          customRender: (text) => text || "--",
        },
        {
          title: this.$t("recette.banque"),
          dataIndex: "bank",
          key: "bank",
          customRender: (text) => text || "--",
        },
        {
          title: this.$t("recette.montant"),
          dataIndex: "montant",
          key: "montant",
          customRender: (text) => Number(text).toFixed(2),
        },
        {
          title: this.$t("tresorerie.statutBanque"),
          dataIndex: "bankStatus",
          key: "bankStatus",
          scopedSlots: { customRender: "bankStatus" },
        },
        {
          title: this.$t("recette.dateEcheance"),
          dataIndex: "dateEchance",
          key: "dateEchance",
          customRender: (text) =>
            text ? moment(text).format("DD/MM/YYYY") : "--",
        },
        {
          title: this.$t("tresorerie.rib"),
          dataIndex: "rib",
          key: "rib",
          scopedSlots: { customRender: "rib" },
        },
      ];
    },
    associatedTPEColumns() {
      return [
        {
          title: this.$t("recette.transactionId"),
          dataIndex: "transactionId",
          key: "transactionId",
          customRender: (text) => text || "--",
        },
        {
          title: this.$t("recette.montant"),
          dataIndex: "montant",
          key: "montant",
          customRender: (text) => Number(text).toFixed(2),
        },
      ];
    },
    associatedVirementsColumns() {
      return [
        {
          title: this.$t("recette.numero"),
          dataIndex: "num",
          key: "num",
          customRender: (text) => text || "--",
        },
        {
          title: this.$t("recette.banque"),
          dataIndex: "bank",
          key: "bank",
          customRender: (text) => text || "--",
        },
        {
          title: this.$t("recette.titulaire"),
          dataIndex: "titulaire",
          key: "titulaire",
          customRender: (text) => text || "--",
        },
        {
          title: this.$t("recette.montant"),
          dataIndex: "montant",
          key: "montant",
          customRender: (text) => Number(text).toFixed(2),
        },
        {
          title: this.$t("tresorerie.statutBanque"),
          dataIndex: "bankStatus",
          key: "bankStatus",
          scopedSlots: { customRender: "bankStatus" },
        },
        {
          title: this.$t("recette.file"),
          dataIndex: "file",
          key: "file",
          scopedSlots: { customRender: "file" },
        },
      ];
    },
    replacedChequesColumns() {
      return [
        {
          title: this.$t("recette.numero"),
          dataIndex: "num",
          key: "num",
          customRender: (text) => text || "--",
        },
        {
          title: this.$t("recette.titulaire"),
          dataIndex: "titulaire",
          key: "titulaire",
          customRender: (text) => text || "--",
        },
        {
          title: this.$t("recette.banque"),
          dataIndex: "bank",
          key: "bank",
          customRender: (text) => text || "--",
        },
        {
          title: this.$t("recette.montant"),
          dataIndex: "montant",
          key: "montant",
          customRender: (text) => Number(text).toFixed(2),
        },
        {
          title: this.$t("tresorerie.statutBanque"),
          dataIndex: "bankStatus",
          key: "bankStatus",
          scopedSlots: { customRender: "bankStatus" },
        },
        {
          title: this.$t("tresorerie.statut"),
          dataIndex: "status",
          key: "status",
          scopedSlots: { customRender: "status" },
        },
        {
          title: this.$t("recette.dateEcheance"),
          dataIndex: "dateEchance",
          key: "dateEchance",
          customRender: (text) =>
            text ? moment(text).format("DD/MM/YYYY") : "--",
        },
        {
          title: this.$t("recette.replacedBy"),
          dataIndex: "replacedBy",
          key: "replacedBy",
          scopedSlots: { customRender: "replacedBy" },
        },
      ];
    },
  },
  methods: {
    moment,
    formatFeeName(key, daily = false) {
      return formatFeeNameUtil(key, daily, this.$t.bind(this));
    },
    getChequeStatutColor,

    getChequeDetails(invoiceId) {
      this.loading = true;
      apiClient
        .get("/compV3/parent-invoice/" + invoiceId)
        .then((res) => {
          console.log(res.data);
          // Handle the response structure - check if it's wrapped in a data property
          const invoiceData = res.data.data || res.data;
          this.loadedInvoice = invoiceData;

          if (invoiceData.cheques) {
            this.loadedChequesData = invoiceData.cheques.filter(
              (e) => e.type === "CHEQUE"
            );
            this.loadedTraitesData = invoiceData.cheques.filter(
              (e) => e.type === "TRAITE"
            );
            this.loadedVirementsData = invoiceData.cheques.filter(
              (e) => e.type === "VIREMENT"
            );
          }
          if (invoiceData.replaceChequeWithCash) {
            this.loadedReplacedChequesData = invoiceData.replaceChequeWithCash;
          }
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    handleOk() {
      this.$emit("close");
      this.loadedChequesData = [];
      this.loadedTraitesData = [];
      this.loadedVirementsData = [];
      this.loadedReplacedChequesData = [];
    },
    previewFile(fileUrl) {
      // Open file in new tab for preview
      if (fileUrl) {
        window.open(fileUrl, "_blank");
      }
    },
  },
  watch: {
    chequeVisible(newVal) {
      this.localChequeVisible = newVal; // Keep the local copy in sync with the prop
    },
  },
};
</script>

<style scoped>
.cheque-modal-content.minimal {
  padding: 16px 0 0 0;
  background: none;
}
.minimal-section {
  margin-bottom: 24px;
  padding: 0 0 8px 0;
  border-bottom: 1px solid #f0f0f0;
}
.minimal-section:last-child {
  border-bottom: none;
}
.cheque-totals {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 8px;
}
.minimal-title {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 12px;
  color: #222;
  display: flex;
  align-items: center;
  gap: 8px;
}
.minimal-label {
  font-weight: 600;
  font-size: 15px;
  margin-right: 8px;
  display: flex;
  align-items: center;
}
.minimal-value {
  font-size: 18px;
  font-weight: bold;
  color: #1890ff;
}
.cheque-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 120px;
}
/* Zebra striping for tables */
::v-deep .zebra-row:nth-child(even) td {
  background: #fafafa !important;
}
::v-deep .zebra-row:nth-child(odd) td {
  background: #fff !important;
}
</style>
